// Centralized error handling utility for Firebase Authentication
import { useState } from 'react';

// Generic Firebase error messages (fallbacks)
const firebaseErrorMessages = {
  'auth/invalid-credential': 'The email or password you entered is incorrect. Please check your credentials and try again.',
  'auth/user-not-found': 'No account found with this email address. Please check the email or sign up for a new account.',
  'auth/wrong-password': 'The password you entered is incorrect. Please try again or use "Recover Password" if you forgot it.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Please contact support for assistance.',
  'auth/too-many-requests': 'Too many failed login attempts. Please wait a few minutes before trying again.',
  'auth/email-already-in-use': 'This email is already in use. Please use a different email.',
  'auth/weak-password': 'The password is too weak. Please use a stronger password.',
  'auth/popup-closed-by-user': 'The popup was closed before completing the sign-in process.',
  'auth/user-cancelled': 'The sign-in process was cancelled.',
  'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
  'auth/account-exists-with-different-credential': 'An account already exists with the same email but different sign-in credentials. Try signing in with a different method.',
};

// Enhanced error messages for specific scenarios
const enhancedErrorMessages = {
  'auth/invalid-credential': {
    login: 'The email or password you entered is incorrect. Please check your credentials and try again.',
    signup: 'Invalid credentials provided. Please check your information.'
  },
  'auth/user-not-found': {
    login: 'No account found with this email address. Please check the email or sign up for a new account.',
    passwordReset: 'No account found with this email address. Please check the email or sign up for a new account.'
  },
  'auth/email-already-in-use': {
    signup: 'An account with this email already exists. Please login instead.',
    signupWithLink: (email) => ({
      message: `An account with ${email} already exists.`,
      link: { text: 'Click here to login', href: '/login' }
    })
  },
  'auth/wrong-password': {
    login: 'The password you entered is incorrect. Please try again or use "Recover Password" if you forgot it.'
  },
  'auth/weak-password': {
    signup: 'Password must be at least 8 characters long and include uppercase, lowercase letters, and a number.'
  },
  'auth/too-many-requests': {
    login: 'Too many failed login attempts. Please wait a few minutes before trying again.',
    passwordReset: 'Too many password reset requests. Please wait a few minutes before trying again.'
  },
  'auth/invalid-login-credentials': {
    login: 'The email or password you entered is incorrect. Please check your credentials and try again.'
  },
  'auth/invalid-verification-code': {
    login: 'Invalid verification code. Please check the code and try again.',
    signup: 'Invalid verification code. Please check the code and try again.'
  },
  'auth/missing-verification-code': {
    login: 'Please enter the verification code sent to your phone.',
    signup: 'Please enter the verification code sent to your phone.'
  },
  'auth/code-expired': {
    login: 'The verification code has expired. Please request a new code.',
    signup: 'The verification code has expired. Please request a new code.'
  },
  'auth/invalid-phone-number': {
    signup: 'Invalid phone number format. Please enter a valid phone number.'
  },
  'auth/missing-phone-number': {
    signup: 'Please enter your phone number for multi-factor authentication.'
  },
  'auth/quota-exceeded': {
    default: 'Too many requests. Please try again later.'
  },
  'auth/captcha-check-failed': {
    login: 'reCAPTCHA verification failed. Please try again.',
    signup: 'reCAPTCHA verification failed. Please refresh the page and try again.'
  },
  'auth/recaptcha-not-enabled': {
    login: 'reCAPTCHA is not enabled for this application. Please contact support.',
    signup: 'reCAPTCHA verification failed. Please refresh the page and try again.'
  },
  'auth/app-not-authorized': {
    signup: 'This app is not authorized to use Firebase Authentication. Please contact support.'
  },
  'auth/email-not-verified': { 
    login: 'Please verify your email address before logging in. Check your inbox for the verification link.',
    signup: 'Please check your email and click the verification link to complete your account setup.'
  },
  // MFA-related error codes
  'auth/mfa-verification-failed': {
    login: 'Invalid verification code. Please try again.'
  },
  'auth/unsupported-mfa-method': {
    login: 'This multi-factor authentication method is not yet supported.'
  },
  'auth/no-mfa-factor-selected': {
    login: 'Please select a verification method.'
  },
  'auth/totp-not-supported': {
    login: 'TOTP authentication is not yet supported.'
  },
  'auth/non-phone-mfa-not-supported': {
    login: 'Non-phone multi-factor authentication is not yet supported.'
  },
  // Session error codes
  'auth/session-expired': {
    login: 'Your session has expired. Please sign in again.'
  },
  // Popup errors
  'auth/popup-closed-by-user': {
    login: 'The popup was closed before completing the sign-in process.',
    signup: 'Sign up was cancelled. Please try again.'
  },
  'auth/popup-blocked': {
    login: 'Popup was blocked. Please allow popups and try again.',
    signup: 'Popup was blocked. Please allow popups and try again.'
  },
  // reCAPTCHA errors
  'auth/recaptcha-expired': {
    signup: 'reCAPTCHA has expired, please try again.'
  },
  // Validation error codes
  'validation/invalid-email': {
    login: 'Please enter a valid email address.',
    signup: 'Please enter a valid email address.',
    passwordReset: 'Please enter a valid email address to recover your password.'
  },
  'validation/missing-password': {
    login: 'Please enter your password.'
  },
  'validation/weak-password': {
    signup: 'Please ensure your password meets all requirements.'
  },
  'validation/password-mismatch': {
    signup: 'Please ensure your passwords match.'
  },
  'validation/invalid-phone': {
    signup: 'Please enter a valid phone number.'
  },
  'validation/invalid-invite-code': {
    signup: 'Sorry, that invite code is not valid, please try again.'
  },
  'validation/missing-invite-code': {
    signup: 'Please enter a valid invite code first.'
  },
  // Profile/document creation errors
  'firestore/profile-creation-failed': {
    signup: 'Failed to create user profile. Please try again.'
  },
  'firestore/verification-email-failed': {
    signup: 'Account created but verification email failed to send. Please contact support or try logging in to resend the verification email.'
  }
};

/**
 * Get the appropriate error message for a Firebase error
 * @param {Error} error - The Firebase error object
 * @param {string} context - The context where the error occurred ('login', 'signup', 'passwordReset')
 * @returns {string|object} The error message or an object with message and link
 */
export const getFirebaseErrorMessage = (error, context = 'default') => {
  const errorCode = error.code || '';
  
  // Check for enhanced messages first
  if (enhancedErrorMessages[errorCode] && enhancedErrorMessages[errorCode][context]) {
    const message = enhancedErrorMessages[errorCode][context];
    return typeof message === 'function' ? message(error.email) : message;
  }
  
  // Fall back to default Firebase error messages
  if (firebaseErrorMessages[errorCode]) {
    return firebaseErrorMessages[errorCode];
  }
  
  // Handle unknown errors with context-specific defaults
  const defaultMessages = {
    login: 'Login failed. Please check your email and password and try again.',
    signup: 'Sign up failed. Please check your information and try again.',
    passwordReset: 'Unable to send password reset email. Please try again later.',
    default: 'An error occurred. Please try again.'
  };
  
  return defaultMessages[context] || defaultMessages.default;
};

/**
 * Create a consistent message state handler
 * @returns {object} Object with message state and handlers
 */
export const useMessageHandler = () => {
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('error');
  
  const showError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageType('error');
  };
  
  const showSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageType('success');
  };
  
  const clearMessage = () => {
    setMessage('');
    setMessageType('error');
  };
  
  const handleFirebaseError = (error, context) => {
    const errorMessage = getFirebaseErrorMessage(error, context);
    showError(errorMessage);
  };
  
  return {
    message,
    messageType,
    showError,
    showSuccess,
    clearMessage,
    handleFirebaseError
  };
};